import {
  Component,
  computed,
  inject,
  Inject,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AbbModalComponent } from '@components/abb-modal/abb-modal.component';
import { MODAL_DATA, ModalData } from '@components/abb-modal/modal';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { SignupFormComponent } from '@pages/signup/signup-form/signup-form.component';
import { Apollo } from 'apollo-angular';
import { CognitoService, UserWithID } from '@services/cognito/cognito.service';
import { VerifiableUserAttributeKey } from 'aws-amplify/auth';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/ui/toast.service';
import { getErrorMessage } from '@utils/errors';
import { CompanyService } from '@services/company/company-service.service';
import { UserAccount } from 'gql/graphql';

export type AccountModalData = {
  mode: 'new' | 'edit';
  user?: UserWithID | UserAccount;
};

@Component({
  selector: 'abb-account-modal',
  standalone: true,
  template: `
    <abb-modal [title]="title[data.mode] | t" #modalRef>
      @if(!showConfirm()) {
      <abb-signup-form
        [disableEmail]="true"
        [mode]="data.mode"
        [initValues]="initValues()"
        (formSubmit)="onSubmit($event)"
        (formValidationStatusChange)="handleSubmitStatusBtn($event)"
      ></abb-signup-form>
      }@else {
      <p class="mb-4">
        {{
          'Confirmation code was sent to {deliveryMedium}'
            | t : { deliveryMedium: deliveryMedium }
        }}.
      </p>
      <form
        id="confirm-attribute-update-form"
        [formGroup]="confirmAttributeUpdateForm"
        (ngSubmit)="confirmUserAttributes()"
      >
        @for(attribute of this.attributesToConfirm; track $index) {
        <mat-form-field class="w-full" color="primary">
          <mat-label>
            {{ 'Code for {attribute}' | t : { attribute: attribute } }}
          </mat-label>
          <input
            matInput
            [formControlName]="attribute + 'Code'"
            [name]="attribute + 'Code'"
          />@if(confirmAttributeUpdateForm.get(attribute+'Code')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>
        }
      </form>
      }
      <div footer class="w-full flex flex-row gap-8 justify-end">
        <abb-button
          variant="clean"
          [disabled]="loading()"
          (onclick)="onCancel()"
          >{{ 'cancel' | t }}</abb-button
        >
        @if (showConfirm()) {
        <abb-button
          class="w-32"
          type="submit"
          [loading]="loading()"
          [form]="'confirm-attribute-update-form'"
          [disabled]="confirmAttributeUpdateForm.invalid || loading()"
        >
          {{ 'Confirm' | t }}</abb-button
        >
        } @else {
        <abb-button
          class="w-32"
          type="submit"
          [loading]="loading()"
          [form]="'signup-form'"
          [disabled]="submitBtnDisabled || loading()"
        >
          {{ 'Save' | t }}</abb-button
        >
        }
      </div>
    </abb-modal>
  `,
  styles: ``,
  imports: [
    AbbModalComponent,
    TPipe,
    AbbButtonComponent,
    SignupFormComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
  ],
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'settings.tabs.account' },
  ],
})
export class AccountModalComponent implements ModalData<AccountModalData> {
  @ViewChild('modalRef') modalRef: AbbModalComponent | undefined;
  prefix = inject(TRANSLATION_PREFIX);
  userUpdated = output<UserAccount>();
  loading = signal(false);
  showConfirm = signal(false);
  deliveryMedium: string | undefined = undefined;
  attributesToConfirm: Array<VerifiableUserAttributeKey>;
  confirmAttributeUpdateForm = new FormGroup({});

  private isUserWithID(user: UserWithID | UserAccount): user is UserWithID {
    return 'given_name' in user;
  }

  private isUserAccount(user: UserWithID | UserAccount): user is UserAccount {
    return 'name' in user && !('given_name' in user);
  }

  /* userAddress = computed<{
    postalCode: string;
    country: string;
    locality: string;
    street_address: string;
  }>(() => JSON.parse(this.data.user?.address!)); */

  constructor(
    @Inject(MODAL_DATA) public data: AccountModalData,
    public apollo: Apollo,
    private cognitoService: CognitoService,
    private translate: TranslateService,
    private toast: ToastService,
    readonly companyService: CompanyService
  ) {
    this.attributesToConfirm = [];
    this.showConfirm.set(false);
    this.loading.set(false);
  }

  readonly title = {
    edit: 'Edit',
    new: 'New',
  };
  readonly initValues = computed(() => {
  if (this.data.mode !== 'edit' || !this.data.user) {
    return undefined;
  }

  const user = this.data.user;

  if (this.isUserWithID(user)) {
    // Gestisce UserWithID (da Cognito)
    return {
      id: user.id,
      name: user.given_name || '',
      lastName: user.family_name || '',
      imgUrl: user.imgUrl || '',
      company: this.companyService.currentCompany(),
      phone: user.phone_number || '',
      email: user.email || '',
    };
  } else if (this.isUserAccount(user)) {
    // Gestisce UserAccount (dal backend)
    return {
      id: user.id,
      name: user.name,
      lastName: user.lastName,
      imgUrl: '', // UserAccount non ha imgUrl
      company: user.company,
      phone: user.phone,
      email: user.email,
    };
  }

  return undefined;
});

  submitBtnDisabled: boolean = true;
  handleSubmitStatusBtn(disabled: boolean): void {
    this.submitBtnDisabled = disabled;
  }

  onCancel() {
    this.modalRef?.dispose();
  }

  onSubmit(form: FormGroup): void {
    if (form.invalid) {
      return;
    }
    this.loading.set(true);

    // Emetti tutti i valori del form, inclusi quelli disabilitati
    this.userUpdated.emit(form.getRawValue());
  }

  confirmUserAttributes(): void {
    if (this.confirmAttributeUpdateForm.invalid) {
      return;
    }
    this.loading.set(true);
    this.attributesToConfirm.map((a: VerifiableUserAttributeKey) => {
      const formValues = this.confirmAttributeUpdateForm.value;
      const attr = (a + 'Code') as keyof typeof formValues;
      this.cognitoService
        .confirmUserAttribute(a, formValues[attr])
        .then(() => {
          this.modalRef?.dispose();
        })
        .catch((err) => {
          this.loading.set(false);
          console.error('Confirm user attributes error:', err);
          let errMsg = getErrorMessage(err, this.translate);
          this.toast.notifyError(errMsg);
        })
        .finally(() => {
          this.loading.set(false);
        });
    });
  }
}
